<template>
  <LibraryLayout>
    <EpisodesBlock
      v-if="currentlyReadingViewRef && currentlyReadingViewRef.dataLength"
      :view="currentlyReadingViewRef"
    />

    <NoCurrentlyReadingDocsBlock v-else title="Reading now">
      <p>
        Podcast episodes you're currently reading will appear here. Find new
        things to read on the Discover page!
      </p>
    </NoCurrentlyReadingDocsBlock>
    <EpisodesBlock
      v-if="favoritesViewRef && favoritesViewRef.dataLength"
      :view="favoritesViewRef"
    />
    <EmptyListingBlock v-else title="Favorites" class="empty-favorite">
      <div>
        <p>Podcast episodes you've marked as a Favorite will appear here.</p>
        <p>
          To mark an episode as a Favorite, click the bookmark icon on the
          podcast card or while reading the podcast.
        </p>
      </div>
    </EmptyListingBlock>
    <EpisodesBlock
      v-if="finishedViewRef && finishedViewRef.dataLength"
      :view="finishedViewRef"
    />
    <NoFinishedDocsBlock v-else>
      <p>Podcast episodes you've finished will appear here.</p>
      <p>
        To mark a podcast episode as Finished, click the three dot menu on the
        episode card, then click "Move to Finished".
      </p>
    </NoFinishedDocsBlock>
  </LibraryLayout>
</template>

<script setup lang="ts">
import { onBeforeMount, Raw, ref } from 'vue'

import EpisodesBlock from '@/app/components/listing/EpisodesBlock.vue'
import NoCurrentlyReadingDocsBlock from '@/app/components/listing/NoCurrentlyReadingDocsBlock.vue'
import NoFinishedDocsBlock from '@/app/components/listing/NoFinishedDocsBlock.vue'
import EmptyListingBlock from '@/app/components/listing/EmptyListingBlock.vue'

import { filteredDocsByDocType } from '@/models/doc'

import { useWait } from '@/app/composables/useWait'
import { Doc } from '@/models/interfaces'
import LibraryLayout from '@/app/components/library/LibraryLayout.vue'
import { PodcastEpisodesView } from '@/models/docs.list.podcasts.episodes'
import { DocSortOption } from '@/models/doc'
import { ListView } from '@/models/docs.list'

const props = defineProps<{
  docs: Doc[]
}>()

const wait = useWait()

const currentlyReadingViewRef = ref<Raw<ListView> | null>(null)
const finishedViewRef = ref<Raw<ListView> | null>(null)
const favoritesViewRef = ref<Raw<ListView> | null>(null)

onBeforeMount(async () => {
  await getBooks()
})

const getBooks = async (): Promise<void> => {
  wait.start('Loading Library')
  try {
    /* TODO: We need to implement offline support for home/docs, #12899.

    if (offline.isOffline) {
       await loadBookListFromNativeStorage()
     } */

    const episodesData = filteredDocsByDocType(props.docs, 'podcast_episode')

    const reading = await PodcastEpisodesView.createView(episodesData)
    reading.enableContinueReading()
    reading.enableActionsButton()
    reading.setList('reading')
    reading.sort(DocSortOption.accessed)
    currentlyReadingViewRef.value = new ListView('Reading Now', reading, {
      viewAllLink: '/app/podcasts/episodes/list/reading',
    })

    const finished = await PodcastEpisodesView.createView(episodesData)
    finished.enableActionsButton()
    finished.setList('finished')
    finished.sort(DocSortOption.accessed)
    finishedViewRef.value = new ListView('Finished', finished, {
      viewAllLink: '/app/podcasts/episodes/list/finished',
    })

    const favorites = await PodcastEpisodesView.createView(episodesData)
    favorites.enableActionsButton()
    favorites.setIsFavorite(true)
    favorites.sort(DocSortOption.accessed)
    favoritesViewRef.value = new ListView('Favorites', favorites, {
      viewAllLink: '/app/podcasts/episodes/list/favorites',
    })

    wait.end('Loading Library')
  } catch (error) {
    if (Sentry) {
      Sentry.captureException(error)
    }
    wait.end('Loading Library')
    wait.start('Error Loading Library')
  }
}
</script>

<style scoped lang="scss">
.empty-favorite {
  :deep(.empty-listing__inner) {
    height: 390px;
    justify-content: center;

    @media only screen and (max-width: $w-tablet-s) {
      height: 335px;
    }
  }
}
</style>
